import type {
  CrossTabDeps,
  CrossTabMessage,
  CrossTabMessageType
} from './types';

type MessageHandler = (payload: unknown, senderId: string) => void;

export class CrossTabStore {
  isMaster = $state(false);
  sessionOwnerTabId = $state<string | null>(null);
  initialized = $state(false);

  private handlers = new Map<CrossTabMessageType, Set<MessageHandler>>();
  private detachChannel: (() => void) | null = null;
  private releaseLock: (() => void) | null = null;
  private abortController: AbortController | null = null;
  private hasPendingRequest = false;
  private destroyed = false;

  constructor(private readonly deps: CrossTabDeps) {}

  get tabId(): string {
    return this.deps.tabId;
  }

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    this.detachChannel = this.deps.channel.onMessage((msg) => {
      if (msg.senderId === this.deps.tabId) return;
      this.handleMessage(msg);
    });

    this.abortController = new AbortController();
    this.acquireLoop();
  }

  /**
   * Keeps this tab in the Web Locks queue while it is not the master.
   * When the callback finishes (this tab stopped being master), we re-queue
   * so a later takeover can succeed. `hasPendingRequest` prevents piling up
   * duplicate requests.
   */
  private acquireLoop(): void {
    if (this.destroyed) return;
    if (!this.abortController) return;
    if (this.hasPendingRequest) return;
    this.hasPendingRequest = true;
    this.deps.locks.request(
      'sq-master',
      async () => {
        this.hasPendingRequest = false;
        if (this.destroyed) return;
        this.isMaster = true;
        await new Promise<void>((resolve) => {
          this.releaseLock = resolve;
        });
        this.isMaster = false;
        this.acquireLoop();
      },
      this.abortController.signal
    );
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    const release = this.releaseLock;
    this.releaseLock = null;
    release?.();
    this.abortController?.abort();
    this.abortController = null;
    this.detachChannel?.();
    this.detachChannel = null;
    this.deps.channel.close();
  }

  on(type: CrossTabMessageType, handler: MessageHandler): () => void {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set();
      this.handlers.set(type, set);
    }
    set.add(handler);
    const target = set;
    return () => {
      target.delete(handler);
    };
  }

  private lastEmitKey: string | null = null;
  private lastEmitAt = 0;

  emit(type: CrossTabMessageType, payload: unknown = null): void {
    // Failsafe: drop identical consecutive messages within 100 ms.
    // Prevents broadcast storms from duplicate/zombie store instances.
    let payloadKey: string;
    try {
      payloadKey = JSON.stringify(payload ?? null);
    } catch {
      payloadKey = '<unserializable>';
    }
    const key = `${type}:${payloadKey}`;
    const now = Date.now();
    if (key === this.lastEmitKey && now - this.lastEmitAt < 100) {
      return;
    }
    this.lastEmitKey = key;
    this.lastEmitAt = now;

    const msg: CrossTabMessage = {
      type,
      payload,
      senderId: this.deps.tabId,
      sentAt: new Date().toISOString()
    };
    this.deps.channel.postMessage(msg);
  }

  /**
   * Follower-side API. Asks the current master to run sync.
   * No-op when this tab is the master.
   */
  requestSync(): void {
    if (this.isMaster) return;
    this.emit('sync:requested', null);
  }

  /**
   * Follower-side API. Asks the current master to release its lock so
   * this tab can become master. The master decides whether to grant
   * (based on session activity, injected via onTakeoverCheck).
   */
  requestTakeover(): void {
    if (this.isMaster) return;
    this.emit('master:takeover-request', null);
  }

  /**
   * Master-side API. Releases the master lock voluntarily. The next
   * queued tab (in Web Locks order) will acquire it automatically.
   * Should only be called after verifying it is safe (no active session).
   */
  releaseMasterLock(): void {
    if (!this.isMaster) return;
    this.emit('master:takeover-granted', null);
    const release = this.releaseLock;
    this.releaseLock = null;
    release?.();
  }

  /**
   * Master-side convenience: responds to a takeover request with a denial.
   */
  denyTakeover(reason: string): void {
    if (!this.isMaster) return;
    this.emit('master:takeover-denied', { reason });
  }

  private handleMessage(msg: CrossTabMessage): void {
    if (msg.type === 'session:started') {
      this.sessionOwnerTabId = msg.senderId;
    } else if (
      msg.type === 'session:finished' ||
      msg.type === 'session:discarded'
    ) {
      this.sessionOwnerTabId = null;
    }

    const handlers = this.handlers.get(msg.type);
    if (!handlers || handlers.size === 0) return;
    const snapshot = Array.from(handlers);
    for (const h of snapshot) h(msg.payload, msg.senderId);
  }
}